import { CHARACTER_PRESETS, LIFESPAN_UNITS, SHIP_PRESET, SPECIES_PRESETS, SPECIES_RELATIONS, STATUS_ORDER } from "@/lib/constants";

const PRESETS = { Characters: CHARACTER_PRESETS, Species: SPECIES_PRESETS, Ships: [SHIP_PRESET] };
let seq = 0;
export const newKey = () => `k${++seq}`;
export const blankItem = () => ({ text: "", page_id: null, note: "" });
export const isFilled = (it) => !!(it.page_id || it.text?.trim());

export const presetOf = (category, title) => (PRESETS[category] || []).find((p) => p.title === title) || null;

const withBlank = (fact) => ({ ...fact, items: fact.items?.length ? fact.items : [blankItem()] });

// Editor shape: Character presets merged in (in order), every fact has at least one input row
export function prepareSections(sections = [], category) {
  let out = sections;
  const list = PRESETS[category];
  if (list) {
    const titles = list.map((p) => p.title);
    const presets = list.map((p) => {
      const ex = sections.find((s) => s.title === p.title);
      const facts = p.facts.map((k) => ex?.facts.find((f) => f.key === k) || { key: k, items: [] });
      return { title: p.title, facts: [...facts, ...(ex?.facts.filter((f) => !p.facts.includes(f.key)) || [])] };
    });
    out = [...presets, ...sections.filter((s) => !titles.includes(s.title))];
  }
  return out.map((s) => ({ ...s, _k: s._k || newKey(), facts: s.facts.map(withBlank) }));
}

// Stored/visible shape: only filled items, facts and sections
export const cleanSections = (sections = []) =>
  sections
    .map((s) => ({
      title: s.title.trim(),
      facts: s.facts
        .map((f) => ({
          key: f.key.trim(),
          items: f.items.filter(isFilled).map((i) => ({ text: i.text.trim(), page_id: i.page_id || null, note: (i.note || "").trim(), role: i.role || null })),
        }))
        .filter((f) => f.key && f.items.length),
    }))
    .filter((s) => s.facts.length);

export const itemPage = (item, pages) => (item.page_id ? pages.find((p) => p.id === item.page_id) : null);

const CARD_HIDDEN = { "Ship Information": ["Ship Name", "Status"], "Species Information": ["Status", "Sapient", "Lifespan"] };
export const cardFacts = (sections, pages) =>
  cleanSections(sections).flatMap((s) => s.facts.filter((f) => !(CARD_HIDDEN[s.title] || []).includes(f.key))).slice(0, 3)
    .map((f) => ({ key: f.key, value: f.items.map((i) => itemPage(i, pages)?.title || i.text).join(", ") }));

const shipFact = (page, key) => page.sections?.find((s) => s.title === "Ship Information")?.facts.find((f) => f.key === key);
export const shipLabel = (page) => shipFact(page, "Status")?.items[0]?.text || "Fanon";
export const shipMemberIds = (page) => [...new Set((shipFact(page, "Characters")?.items || []).map((i) => i.page_id).filter(Boolean))];
export const isOtp = (ship, charId) => (ship.otp_by || []).includes(charId);
const byName = (a, b) => a.title.localeCompare(b.title);
export const SHIP_SORTERS = { status: (a, b) => STATUS_ORDER.indexOf(shipLabel(a)) - STATUS_ORDER.indexOf(shipLabel(b)) || byName(a, b), az: byName };
export const shipMembers = (page, pages) => (shipFact(page, "Characters")?.items || []).filter(isFilled)
  .map((i) => ({ item: i, page: itemPage(i, pages) }));
export const pairingLine = (page, pages) => shipMembers(page, pages)
  .map((m) => (m.page ? m.page.title.trim().split(/\s+/)[0] : m.item.text.trim()))
  .join("/");
export const shipsOf = (pages, charId) => pages.filter((p) => p.category === "Ships" && shipMembers(p, pages).some((m) => m.page?.id === charId));

const speciesFact = (page, key) => page.sections?.find((s) => s.title === "Species Information")?.facts.find((f) => f.key === key);
export const speciesStatus = (page) => speciesFact(page, "Status")?.items[0]?.text || "Extant";
export const isSapient = (page) => speciesFact(page, "Sapient")?.items[0]?.text === "Sapient";
export const lifespanOf = (page) => speciesFact(page, "Lifespan")?.items[0]?.text || "";
export const speciesMembers = (pages, id) => pages.filter((p) => p.category === "Characters" && p.sections?.some((s) =>
  s.title === "Biographical Information" && s.facts.some((f) => f.key === "Species" && f.items.some((i) => i.page_id === id))));

const REL = Object.fromEntries(SPECIES_RELATIONS.map((r) => [r[0], r]));
export const relationLabel = (role, side) => (REL[role] ? REL[role][side === "in" ? 2 : 3] : "Related");
// Links from this page plus links pointing at it, worded from this page's point of view
export const relatedSpecies = (pages, page) => {
  const species = pages.filter((p) => p.category === "Species");
  const out = (speciesFact(page, "Related Species")?.items || []).map((i) => ({ page: species.find((p) => p.id === i.page_id), label: relationLabel(i.role, "out") }));
  const inc = species.filter((p) => p.id !== page.id).flatMap((p) => (speciesFact(p, "Related Species")?.items || [])
    .filter((i) => i.page_id === page.id).map((i) => ({ page: p, label: relationLabel(i.role, "in") })));
  const seen = new Set();
  return [...out, ...inc].filter((r) => r.page && r.page.id !== page.id && !seen.has(r.page.id) && seen.add(r.page.id));
};

const LIFE_RE = new RegExp(`^(\\d+(?:\\.\\d+)?)(?:\\s*[–-]\\s*(\\d+(?:\\.\\d+)?))?\\s+(${LIFESPAN_UNITS.join("|")})$`);
export const parseLifespan = (text = "") => {
  if (!text.trim()) return { kind: "", min: "", max: "", unit: "years", other: "" };
  if (text.trim() === "Immortal") return { kind: "immortal", min: "", max: "", unit: "years", other: "" };
  const m = text.trim().match(LIFE_RE);
  return m ? { kind: "range", min: m[1], max: m[2] || "", unit: m[3], other: "" } : { kind: "other", min: "", max: "", unit: "years", other: text };
};
export const formatLifespan = (l) => {
  if (l.kind === "immortal") return "Immortal";
  if (l.kind === "other") return l.other;
  if (l.kind !== "range" || !l.min) return "";
  const [lo, hi] = l.max && Number(l.max) < Number(l.min) ? [l.max, l.min] : [l.min, l.max];
  return `${lo}${hi && hi !== lo ? `–${hi}` : ""} ${l.unit}`;
};

export const linkedIds = (sections = []) => sections.flatMap((s) => s.facts.flatMap((f) => f.items.map((i) => i.page_id).filter(Boolean)));

// Same pair + same type recorded both on the map and in an infobox -> one bond
export const dedupeRels = (rels) => {
  const seen = new Set();
  return rels.filter((r) => {
    const k = [r.source_id, r.target_id].sort().join("|") + r.type + (r.ship_id || "");
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};
